import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

export const useNavigationHistory = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPathRef = useRef<string>('');
  const lastScrollYRef = useRef<number>(0);

  // Track scroll position continuously
  useEffect(() => {
    history.scrollRestoration = 'manual';

    const onScroll = () => {
      lastScrollYRef.current = window.scrollY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Save on page hide/unload as a safeguard
    const saveOnHide = () => {
      const scrollPositions = JSON.parse(localStorage.getItem('scrollPositions') || '{}');
      const currentPath = location.pathname;
      scrollPositions[currentPath] = lastScrollYRef.current;
      localStorage.setItem('scrollPositions', JSON.stringify(scrollPositions));
    };

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') saveOnHide();
    });
    window.addEventListener('beforeunload', saveOnHide);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('beforeunload', saveOnHide);
    };
  }, [location.pathname]);

  // Handle navigation + history + scroll restoration
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // Load persisted stores
    const navigationHistory: string[] = JSON.parse(localStorage.getItem('navigationHistory') || '[]');
    const scrollPositions: Record<string, number> = JSON.parse(localStorage.getItem('scrollPositions') || '{}');

    // Persist previous page scroll position before changing the viewport
    if (previousPath) {
      scrollPositions[previousPath] = lastScrollYRef.current;
      localStorage.setItem('scrollPositions', JSON.stringify(scrollPositions));
    }

    if (navigationType === 'POP') {
      // Back/forward navigation: restore previous scroll
      const y = typeof scrollPositions[currentPath] === 'number' ? scrollPositions[currentPath] : 0;
      setTimeout(() => window.scrollTo(0, y), 0);
    } else {
      // Forward navigation: go to top
      window.scrollTo(0, 0);
    }

    // Don't store consecutive duplicates
    if (navigationHistory[navigationHistory.length - 1] !== currentPath) {
      navigationHistory.push(currentPath);
      if (navigationHistory.length > 50) navigationHistory.shift();
      localStorage.setItem('navigationHistory', JSON.stringify(navigationHistory));
    }

    // Update previous path ref
    previousPathRef.current = currentPath;
  }, [location.pathname, navigationType]);

  const getPreviousRoute = (skip = 1) => {
    const navigationHistory = JSON.parse(localStorage.getItem('navigationHistory') || '[]');
    const previousIndex = navigationHistory.length - 1 - skip;
    return previousIndex >= 0 ? navigationHistory[previousIndex] : '/';
  };

  const getReviewBackRoute = () => {
    const navigationHistory: string[] = JSON.parse(localStorage.getItem('navigationHistory') || '[]');
    for (let i = navigationHistory.length - 2; i >= 0; i--) {
      const route = navigationHistory[i];
      if (route.includes('/artists/') || route.includes('/tours/') || route === '/' || route === '/activity') {
        return route;
      }
    }
    return '/user-reviews';
  };

  return { getPreviousRoute, getReviewBackRoute };
};